// src/features/system/developer/components/video-gallery/api.ts
//
// Uses the typed `bff` client (NOT the legacy fetchData helper) so that the
// global `X-Tenant-Override` header installed by <TenantProvider> for
// superadmins flows automatically to every gallery request. This is the
// single integration point that makes Developer → Videos respect the
// currently-selected active tenant.
import { bff, BffError } from '@/lib/http/bff';
import { consumeNdjsonStream } from '@/utils/ndjsonStream';
import type { BackfillSidecarsResponse, DeleteAllVideosResponse, DeleteVideoResponse, PlaybackUrlResponse, PosterUrlResponse, PreviewUrlResponse, VideoDetailResponse, VideoListResponse } from './types';

const BASE = '/services/video-pipeline'; // bff client base is already '/bff'

/**
 * Decode the `{ ok:false, error, detail, retry_after_ms, dispatched }`
 * payload from either:
 *   (a) a successful HTTP response that the BFF returned with status
 *       200 + `{ ok:false }` body (legacy soft-fail shape), OR
 *   (b) a thrown `BffError` whose `.body` carries the same JSON
 *       (the typical Fastify path — 4xx/5xx are HTTP errors).
 *
 * Single chokepoint so every per-asset "not ready" classifier is
 * blind to which transport shape happened to arrive. Returns null
 * when the input is not a recognisable BFF error payload (caller
 * should re-throw as-is).
 */
function extractBffErrorBody(input: unknown):
  | { ok: false; error?: string; detail?: string; retry_after_ms?: unknown; dispatched?: unknown }
  | null {
  // Path (a): plain object that already looks like { ok:false, ... }
  if (input && typeof input === 'object' && (input as { ok?: unknown }).ok === false) {
    return input as { ok: false; error?: string; detail?: string; retry_after_ms?: unknown; dispatched?: unknown };
  }
  // Path (b): BffError with a JSON body.
  if (input instanceof BffError) {
    const body = input.body;
    if (body && typeof body === 'object') {
      return { ok: false, ...(body as Record<string, unknown>) } as {
        ok: false; error?: string; detail?: string; retry_after_ms?: unknown; dispatched?: unknown;
      };
    }
  }
  return null;
}

/**
 * Resolve the BFF route base depending on whether we're rendering the
 * authenticated developer surface or a public shared link. Public links
 * are zero-trust: the token IS the credential, and only GET endpoints
 * are accessible. Centralising the path resolution here keeps every
 * caller agnostic of the trust boundary and guarantees we never leak a
 * stale authenticated path into a public-link render tree (DRY).
 */
function routeBase(token?: string | null): string {
  return token ? `/shared/${encodeURIComponent(token)}/video-pipeline` : BASE;
}

export async function fetchVideoList(opts: {
  limit?: number;
  includeInitiated?: boolean;
  view?: 'index' | 'full';
  token?: string;
} = {}): Promise<VideoListResponse> {
  const qs: Record<string, string | number | boolean | undefined> = { view: opts.view ?? 'index' };
  if (opts.limit) qs.limit = opts.limit;
  if (opts.includeInitiated) qs.includeInitiated = 'true';
  const res = await bff.get<VideoListResponse>(`${routeBase(opts.token)}/list`, qs) as any;
  if (res && res.ok === false) throw new Error(res.error || 'video_list_failed');
  return res as VideoListResponse;
}

// ── NDJSON streaming variant ──────────────────────────────────────
// Consumes GET /list/stream — receives a `meta` frame (instant), an
// optional `chunk(phase:'cache')` frame from the BFF's in-process
// SWR cache (TTFB-critical paint), a canonical `chunk(phase:'final')`
// frame once the SWR / cold compute settles, and a terminal `done`
// frame.  Resolves with the FINAL canonical payload so callers that
// just want `Promise<VideoListResponse>` get a drop-in replacement
// for `fetchVideoList`.  Callers wanting early-paint may pass an
// `onCache` handler that fires when (and only when) the cache frame
// arrives BEFORE the final frame.
//
// Error semantics mirror `fetchVideoList`: thrown `Error` on
// transport failure, BFF `error` frame, or terminal `done` never
// arriving before stream end.  Auth / 401 surfaces as a thrown
// `Error('session expired')` so existing `isAuthError` predicates in
// the hook layer still classify it correctly.
export interface FetchVideoListStreamOptions {
  limit?: number;
  includeInitiated?: boolean;
  view?: 'index' | 'full';
  token?: string;
  cursor?: string;
  signal?: AbortSignal;
  /** Fires when the BFF emits its cache snapshot frame (instant paint). */
  onCache?: (payload: VideoListResponse, meta: { servedFrom: string }) => void;
}

interface StreamFrame {
  v?: number;
  type: 'meta' | 'chunk' | 'done' | 'error';
  phase?: 'cache' | 'final';
  served_from?: string;
  code?: string;
  message?: string;
  items?: VideoListResponse['items'];
  total?: number;
  totalBytes?: number;
  next_cursor?: string | null;
  classification?: VideoListResponse['classification'];
  buckets?: VideoListResponse['buckets'];
}

export async function fetchVideoListStream(
  opts: FetchVideoListStreamOptions = {},
): Promise<VideoListResponse> {
  const qs = new URLSearchParams();
  qs.set('view', opts.view ?? 'index');
  if (opts.limit) qs.set('limit', String(opts.limit));
  if (opts.includeInitiated) qs.set('includeInitiated', 'true');
  if (opts.cursor) qs.set('cursor', opts.cursor);

  const url = `${bff.origin}${bff.basePath}${routeBase(opts.token)}/list/stream?${qs.toString()}`;

  const res = await fetch(url, {
    method:      'GET',
    credentials: 'include',
    headers: {
      'accept':          'application/x-ndjson',
      // Brotli/gzip negotiated automatically by the browser.
      ...bff.getAuthHeaders(),
    },
    signal: opts.signal,
  });

  if (res.status === 401) throw new Error('session expired');
  if (!res.ok) {
    throw new Error(`video_list_stream_http_${res.status}`);
  }

  let final:    VideoListResponse | null = null;
  let cacheHit: VideoListResponse | null = null;
  let errorFrame: { code: string; message: string } | null = null;
  let sawDone = false;

  await consumeNdjsonStream<StreamFrame>(res, {
    signal: opts.signal,
    onFrame: (frame) => {
      switch (frame.type) {
        case 'meta':
          break;
        case 'chunk': {
          const payload: VideoListResponse = {
            ok:             true,
            view:           opts.view ?? 'index',
            items:          frame.items          ?? [],
            total:          frame.total          ?? 0,
            totalBytes:     frame.totalBytes     ?? 0,
            next_cursor:    frame.next_cursor    ?? null,
            classification: frame.classification ?? undefined,
            buckets:        frame.buckets        ?? [],
            served_from:    frame.served_from,
          } as VideoListResponse;
          if (frame.phase === 'cache') {
            cacheHit = payload;
            // Fire only when we have not yet seen the final frame; this
            // guards a race where the BFF emitted cache + final back-to-back
            // and we have already resolved.
            if (!final && opts.onCache) {
              try { opts.onCache(payload, { servedFrom: frame.served_from ?? 'cache' }); }
              catch { /* host error swallowed — instant paint is best-effort */ }
            }
          } else {
            final = payload;
          }
          break;
        }
        case 'done':
          sawDone = true;
          break;
        case 'error':
          errorFrame = {
            code:    frame.code    ?? 'video_list_stream_error',
            message: frame.message ?? 'unknown',
          };
          break;
      }
    },
  });

  if (errorFrame) {
    const ef = errorFrame as { code: string; message: string };
    throw new Error(`${ef.code}: ${ef.message}`);
  }
  if (!sawDone && !final) {
    // Stream ended before `done` — treat as transport failure.
    throw new Error('video_list_stream_truncated');
  }
  const result = final ?? cacheHit;
  if (!result) {
    // `done` arrived but no chunk frame ever carried a payload — treat as
    // an empty/transport failure rather than returning a null body.
    throw new Error('video_list_stream_truncated');
  }
  return result;
}

export async function fetchVideoDetail(videoId: string, opts: { token?: string } = {}): Promise<VideoDetailResponse> {
  if (!videoId) throw new Error('videoId required');
  const res = await bff.get<VideoDetailResponse>(
    `${routeBase(opts.token)}/${encodeURIComponent(videoId)}/detail`,
  ) as any;
  if (res && res.ok === false) {
    const msg = [res.error || 'video_detail_failed', res.detail || ''].filter(Boolean).join(': ');
    throw new Error(msg);
  }
  return res as VideoDetailResponse;
}

export async function fetchPlaybackUrl(videoId: string, opts: { token?: string } = {}): Promise<PlaybackUrlResponse> {
  if (!videoId) throw new Error('videoId required');
  const path = `${routeBase(opts.token)}/${encodeURIComponent(videoId)}/playback-url`;

  const fetchOnce = (timeoutMs: number) => bff.request<PlaybackUrlResponse>(path, {
    method: 'GET',
    timeoutMs,
  }) as any;

  // Classifier shared between the success path (HTTP 200 + `{ok:false}`)
  // and the throw path (HTTP 4xx → BffError) so the not-ready overlay
  // never depends on which transport shape the BFF happens to use. Was
  // dead-code on real 404s before — `bff.request` throws on `!res.ok`,
  // so the legacy `res.ok === false` branch never ran.
  const reclassifyNotReady = (payload: ReturnType<typeof extractBffErrorBody>) => {
    if (!payload) return null;
    const code = String(payload.error || '');
    if (code === 'playback_not_found'
        || code === 'playback_not_ready'
        || code === 'playback_not_ready_upload_identity') {
      const retryAfterMs = Number.isFinite(payload.retry_after_ms as number) ? Number(payload.retry_after_ms) : undefined;
      const dispatched   = typeof payload.dispatched === 'boolean' ? payload.dispatched : undefined;
      return new PlaybackNotReadyError(videoId, code as PlaybackNotReadyError['code'], payload.detail, { retryAfterMs, dispatched });
    }
    return null;
  };

  let res: any;
  try {
    // Playback URL minting can include fallback resolution against scope history
    // and object-store rescue paths; default 12s is too strict for these flows.
    res = await fetchOnce(45_000);
  } catch (err: any) {
    const name = String(err?.name || '');
    const msg = String(err?.message || '');
    const timedOut = name === 'TimeoutError' || /timed out/i.test(msg);
    if (timedOut) {
      // One retry with a slightly larger budget to absorb transient control-plane spikes.
      try {
        res = await fetchOnce(60_000);
      } catch (err2: any) {
        const notReady = reclassifyNotReady(extractBffErrorBody(err2));
        if (notReady) throw notReady;
        throw err2;
      }
    } else {
      const notReady = reclassifyNotReady(extractBffErrorBody(err));
      if (notReady) throw notReady;
      throw err;
    }
  }

  // Legacy soft-fail path — HTTP 200 + `{ ok:false }`.
  if (res && res.ok === false) {
    const notReady = reclassifyNotReady(extractBffErrorBody(res));
    if (notReady) throw notReady;
    const code = String(res.error || '');
    const msg = [code || 'playback_url_failed', res.detail || ''].filter(Boolean).join(': ');
    throw new Error(msg);
  }
  return res as PlaybackUrlResponse;
}

/**
 * Sentinel thrown by `fetchPlaybackUrl` when the BFF returns 404 because
 * the underlying video row exists but has no GCS-materialised object
 * yet (or is mid-fallback resolution and the rescue tiers found
 * nothing). Distinct from a plain network/5xx error because the cure
 * is "wait & retry", not "show a broken-video chrome".
 *
 * Three subcodes map onto distinct healing strategies:
 *
 *  • `playback_not_ready`                  — generic transient.
 *  • `playback_not_ready_upload_identity`  — entity exists, gcs_uri
 *    missing. Heals naturally as the resumable PUT completes; the
 *    `upload.completed` Pub/Sub event will populate gcs_uri.
 *  • `playback_not_found`                  — entity not in scope tree
 *    for this tenant. Could heal during a reconcile/backfill sweep
 *    or never heal (genuine orphan). Same FE behaviour either way:
 *    bounded jittered retry, then hide.
 *
 * Mirrors [PosterNotReadyError] / [PreviewNotReadyError] so the
 * hook layer's lazy-retry plumbing can treat all three symmetrically.
 */
export class PlaybackNotReadyError extends Error {
  code: 'playback_not_found' | 'playback_not_ready' | 'playback_not_ready_upload_identity';
  videoId: string;
  /** BFF-provided suggested wait before retrying. FE adds ±20 % jitter. */
  retryAfterMs?: number;
  /** True when the BFF dispatched a self-heal Pub/Sub message on this read. */
  dispatched?: boolean;
  constructor(
    videoId: string,
    code: 'playback_not_found' | 'playback_not_ready' | 'playback_not_ready_upload_identity',
    detail?: string,
    extras?: { retryAfterMs?: number; dispatched?: boolean },
  ) {
    super(`playback object unavailable for video ${videoId}${detail ? `: ${detail}` : ''}`);
    this.name = 'PlaybackNotReadyError';
    this.code = code;
    this.videoId = videoId;
    if (extras?.retryAfterMs !== undefined) this.retryAfterMs = extras.retryAfterMs;
    if (extras?.dispatched   !== undefined) this.dispatched   = extras.dispatched;
  }
}

/**
 * Sentinel thrown by `fetchPosterUrl` when the BFF returns 404 (the
 * worker has not yet written the poster sidecar for this video).
 * Callers branch on `err.code === 'poster_not_ready'` to render the
 * static placeholder without surfacing an error toast.
 */
export class PosterNotReadyError extends Error {
  code: 'poster_not_ready' | 'poster_not_found';
  videoId: string;
  /**
   * Server-suggested wait before the FE retries. Present when the BFF
   * returned a 202 (lazy-heal dispatched). Undefined for hard 404s.
   * Hook layer treats it as an upper bound — actual delay also gets
   * ±20% jitter to spread thundering-herd retries across a feed.
   */
  retryAfterMs?: number;
  /**
   * True when the BFF self-healed by publishing a fresh faststart
   * Pub/Sub message in response to this read. False when the dispatch
   * was deduped (already in flight) or rate-limited.
   */
  dispatched?: boolean;
  constructor(
    videoId: string,
    code: 'poster_not_ready' | 'poster_not_found',
    detail?: string,
    extras?: { retryAfterMs?: number; dispatched?: boolean },
  ) {
    super(`poster sidecar unavailable for video ${videoId}${detail ? `: ${detail}` : ''}`);
    this.name = 'PosterNotReadyError';
    this.code = code;
    this.videoId = videoId;
    if (extras?.retryAfterMs !== undefined) this.retryAfterMs = extras.retryAfterMs;
    if (extras?.dispatched   !== undefined) this.dispatched   = extras.dispatched;
  }
}

/**
 * Mint a short-lived V4 signed READ url for the per-video poster JPEG.
 *
 * Failure semantics:
 *   • 404 from BFF → throws `PosterNotReadyError` (NOT a hard error).
 *     The card renders its existing placeholder; UI never blocks on this.
 *   • 5xx / network → throws a plain `Error`. Caller decides whether to
 *     retry or fall back. The hook caches negatives briefly so we don't
 *     hammer the BFF when an entire feed has no posters yet.
 *
 * No retry-on-timeout here — posters are best-effort and the user has
 * already seen the placeholder. A second 45s+60s round trip would only
 * delay the eventual placeholder->image swap if the BFF is unhealthy.
 */
export async function fetchPosterUrl(videoId: string, opts: { token?: string } = {}): Promise<PosterUrlResponse> {
  if (!videoId) throw new Error('videoId required');
  const path = `${routeBase(opts.token)}/${encodeURIComponent(videoId)}/poster-url`;
  const reclassifyNotReady = (payload: ReturnType<typeof extractBffErrorBody>) => {
    if (!payload) return null;
    const code = String(payload.error || '');
    if (code === 'poster_not_found' || code === 'poster_not_ready') {
      const retryAfterMs = Number.isFinite(payload.retry_after_ms as number) ? Number(payload.retry_after_ms) : undefined;
      const dispatched   = typeof payload.dispatched === 'boolean' ? payload.dispatched : undefined;
      return new PosterNotReadyError(videoId, code as PosterNotReadyError['code'], payload.detail, { retryAfterMs, dispatched });
    }
    return null;
  };
  let res: any;
  try {
    res = await bff.request<PosterUrlResponse>(path, { method: 'GET', timeoutMs: 15_000 }) as any;
  } catch (err: any) {
    const notReady = reclassifyNotReady(extractBffErrorBody(err));
    if (notReady) throw notReady;
    throw err;
  }
  if (res && res.ok === false) {
    const notReady = reclassifyNotReady(extractBffErrorBody(res));
    if (notReady) throw notReady;
    const code = String(res.error || '');
    const msg = [code || 'poster_url_failed', res.detail || ''].filter(Boolean).join(': ');
    throw new Error(msg);
  }
  return res as PosterUrlResponse;
}

/**
 * Sentinel thrown by `fetchPreviewUrl` when the BFF returns 404 (the
 * worker has not yet written the preview-clip sidecar for this video).
 * Callers branch on `err.code === 'preview_not_ready'` to keep the
 * static poster visible without surfacing an error toast. Hover
 * preview is an enhancement — missing it is never a hard error.
 */
export class PreviewNotReadyError extends Error {
  code: 'preview_not_ready' | 'preview_not_found';
  videoId: string;
  retryAfterMs?: number;
  dispatched?: boolean;
  constructor(
    videoId: string,
    code: 'preview_not_ready' | 'preview_not_found',
    detail?: string,
    extras?: { retryAfterMs?: number; dispatched?: boolean },
  ) {
    super(`preview sidecar unavailable for video ${videoId}${detail ? `: ${detail}` : ''}`);
    this.name = 'PreviewNotReadyError';
    this.code = code;
    this.videoId = videoId;
    if (extras?.retryAfterMs !== undefined) this.retryAfterMs = extras.retryAfterMs;
    if (extras?.dispatched   !== undefined) this.dispatched   = extras.dispatched;
  }
}

/**
 * Mint a short-lived V4 signed READ url for the per-video preview MP4.
 *
 * Failure semantics:
 *   • 404 from BFF → throws `PreviewNotReadyError` (NOT a hard error).
 *     The card keeps its poster visible; hover preview silently no-ops.
 *   • 5xx / network → throws a plain `Error`. The hook layer caches
 *     negatives briefly so we don't hammer the BFF when an entire feed
 *     has no previews yet (e.g. during the first minutes after upload
 *     while the worker is still chewing through the backlog).
 *
 * No retry on timeout — hover preview is a progressive enhancement.
 * A second 30s round trip would only delay the eventual poster->video
 * swap if the BFF is unhealthy, and the user has already moved on.
 */
export async function fetchPreviewUrl(videoId: string, opts: { token?: string } = {}): Promise<PreviewUrlResponse> {
  if (!videoId) throw new Error('videoId required');
  const path = `${routeBase(opts.token)}/${encodeURIComponent(videoId)}/preview-url`;
  const reclassifyNotReady = (payload: ReturnType<typeof extractBffErrorBody>) => {
    if (!payload) return null;
    const code = String(payload.error || '');
    if (code === 'preview_not_found' || code === 'preview_not_ready') {
      const retryAfterMs = Number.isFinite(payload.retry_after_ms as number) ? Number(payload.retry_after_ms) : undefined;
      const dispatched   = typeof payload.dispatched === 'boolean' ? payload.dispatched : undefined;
      return new PreviewNotReadyError(videoId, code as PreviewNotReadyError['code'], payload.detail, { retryAfterMs, dispatched });
    }
    return null;
  };
  let res: any;
  try {
    res = await bff.request<PreviewUrlResponse>(path, { method: 'GET', timeoutMs: 15_000 }) as any;
  } catch (err: any) {
    const notReady = reclassifyNotReady(extractBffErrorBody(err));
    if (notReady) throw notReady;
    throw err;
  }
  if (res && res.ok === false) {
    const notReady = reclassifyNotReady(extractBffErrorBody(res));
    if (notReady) throw notReady;
    const code = String(res.error || '');
    const msg = [code || 'preview_url_failed', res.detail || ''].filter(Boolean).join(': ');
    throw new Error(msg);
  }
  return res as PreviewUrlResponse;
}

export async function deleteVideo(videoId: string): Promise<DeleteVideoResponse> {
  if (!videoId) throw new Error('videoId required');
  const res = await bff.request<DeleteVideoResponse>(
    `${BASE}/${encodeURIComponent(videoId)}`,
    {
      method: 'DELETE',
      timeoutMs: 2 * 60 * 1000,
    },
  ) as any;
  if (res && res.ok === false) {
    const msg = [res.error || 'video_delete_failed', res.detail || ''].filter(Boolean).join(': ');
    throw new Error(msg);
  }
  return res as DeleteVideoResponse;
}

/**
 * Tenant-bulk destructive delete. Server re-enforces the `DELETE ALL`
 * literal on the body (defence in depth — UI gate is not the only barrier).
 *
 * Uses an extended client timeout (5 min) because the server iterates
 * every entity for the caller's tenant. The server-side path is fast
 * (orchestrator-only deletes; GCS cleanup is fire-and-forget) but a
 * tenant with thousands of rows can still exceed the default fetch
 * timeout. Object-store reclamation continues asynchronously after
 * the response — see `video.upload.delete_all.gcs_completed` event.
 */
export async function deleteAllVideos(confirm: string): Promise<DeleteAllVideosResponse> {
  const res = await bff.post<DeleteAllVideosResponse, { confirm: string }>(
    `${BASE}/delete-all`,
    { confirm },
    { timeoutMs: 5 * 60 * 1000 },
  ) as any;
  if (res && res.ok === false) {
    const msg = [res.error || 'video_delete_all_failed', res.detail || ''].filter(Boolean).join(': ');
    throw new Error(msg);
  }
  return res as DeleteAllVideosResponse;
}

// ── Reconcile (self-heal stuck uploads) ─────────────────────────────────
// Reconcile is the FE-side counterpart to the server-driven recovery sweep.
// Two flavours:
//   • runReconcileSweep()      — bounded sweep across the active tenant.
//   • recoverVideo(videoId)    — single-entity recover for a stuck card.
//
// Both endpoints are idempotent server-side: re-running on a healthy entity
// is a no-op (`action: 'skip_healthy'`). Wall-clock bounded so the FE never
// hangs on a slow orchestrator round-trip.
export interface ReconcileCounts {
  total: number;
  skip_healthy: number;
  skip_in_flight: number;
  skip_no_bytes: number;
  skip_invalid: number;
  promote_and_start: number;
  restart_workflow: number;
  failed: number;
}
export interface ReconcileSweepResponse {
  ok: boolean;
  started_at: string;
  finished_at: string;
  duration_ms: number;
  examined: number;
  counts: Partial<ReconcileCounts>;
  budget_exceeded: boolean;
  dry_run: boolean;
  sample?: Array<{ ok: boolean; action: string; reason?: string; video_id: string | null }>;
}
export interface ReconcileSingleResponse {
  ok: boolean;
  action: string;
  reason?: string;
  video_id: string | null;
  run_id?: string;
  promote_failed?: boolean;
}

export async function runReconcileSweep(opts: { limit?: number; dryRun?: boolean } = {}): Promise<ReconcileSweepResponse> {
  const res = await bff.post<ReconcileSweepResponse, { limit?: number; dryRun?: boolean }>(
    `${BASE}/reconcile`,
    { limit: opts.limit, dryRun: opts.dryRun },
    { timeoutMs: 90_000 }, // matches server-side RECONCILE_SWEEP_BUDGET_MS + slack
  ) as any;
  if (res && res.ok === false && !res.counts) {
    const msg = [res.error || 'reconcile_failed', res.detail || ''].filter(Boolean).join(': ');
    throw new Error(msg);
  }
  return res as ReconcileSweepResponse;
}

export async function recoverVideo(videoId: string): Promise<ReconcileSingleResponse> {
  if (!videoId) throw new Error('videoId required');
  const res = await bff.post<ReconcileSingleResponse, Record<string, never>>(
    `${BASE}/reconcile/${encodeURIComponent(videoId)}`,
    {} as Record<string, never>,
    { timeoutMs: 30_000 },
  ) as any;
  if (res && res.ok === false) {
    const msg = [res.error || 'reconcile_failed', res.detail || ''].filter(Boolean).join(': ');
    throw new Error(msg);
  }
  return res as ReconcileSingleResponse;
}

// ── Operator orphan purge ───────────────────────────────────────────────
// Operator-driven bulk-delete for orphan/phantom scope rows within the
// caller's tenant. Internally drives the same GC decider as the cron, so
// healthy rows are PHYSICALLY impossible to delete — there is no
// `force_delete_healthy`. Two-gate confirmation: the FE sends the literal
// "PURGE ORPHANS", the server re-checks it (defence in depth).
//
// Use `dryRun: true` to preview the count without writes — the response
// `counts` field reports what WOULD have been deleted.
export interface GcCounts {
  total: number;
  skip_healthy: number;
  skip_in_flight: number;
  skip_young: number;
  skip_not_eligible: number;
  delete_scope_only: number;
  delete_both: number;
  failed: number;
}
export interface PurgeOrphansResponse {
  ok: boolean;
  started_at: string;
  finished_at: string;
  duration_ms: number;
  examined: number;
  counts: Partial<GcCounts>;
  budget_exceeded: boolean;
  dry_run: boolean;
  force: boolean;
  sample?: Array<{ ok: boolean; action: string; reason?: string; video_id: string | null }>;
}

export const PURGE_ORPHANS_CONFIRMATION = 'PURGE ORPHANS';

export async function purgeOrphans(
  opts: { confirm: string; dryRun?: boolean; limit?: number },
): Promise<PurgeOrphansResponse> {
  const res = await bff.post<
    PurgeOrphansResponse,
    { confirm: string; dryRun?: boolean; limit?: number }
  >(
    `${BASE}/purge-orphans`,
    { confirm: opts.confirm, dryRun: opts.dryRun, limit: opts.limit },
    { timeoutMs: 90_000 }, // matches server-side GC_SWEEP_BUDGET_MS + slack
  ) as any;
  if (res && res.ok === false && !res.counts) {
    const msg = [res.error || 'purge_orphans_failed', res.detail || ''].filter(Boolean).join(': ');
    throw new Error(msg);
  }
  return res as PurgeOrphansResponse;
}

export const BACKFILL_SIDECARS_CONFIRMATION = 'BACKFILL SIDECARS';

export async function backfillSidecars(
  opts: { confirm: string; dryRun?: boolean; limit?: number },
): Promise<BackfillSidecarsResponse> {
  const res = await bff.post<BackfillSidecarsResponse, { confirm: string; dryRun?: boolean; limit?: number }>(
    `${BASE}/backfill-sidecars`,
    { confirm: opts.confirm, dryRun: opts.dryRun, limit: opts.limit },
    { timeoutMs: 90_000 },
  ) as any;
  if (res && res.ok === false && !res.counts) {
    const msg = [res.error || 'backfill_sidecars_failed', res.detail || ''].filter(Boolean).join(': ');
    throw new Error(msg);
  }
  return res as BackfillSidecarsResponse;
}
